/**
 * 共享密鑰（VUE_APP_API_SIGNATURE_KEY）的靜態加密工具。
 *
 * 目的：倉庫裡只存密文（enc: 開頭），明文只在「真正要用鑰匙的時候」於記憶體中還原
 * （見 src/utils/api-signature.js 的 getApiKey()），所以程式碼倉庫與程式碼評審都不會
 * 再出現明文密鑰。
 *
 * 加密規則：UTF-8 位元組 → 前面附 2 位元組校驗碼 → 與固定混淆鹽循環 XOR → 標準 Base64；
 * 密文格式 <code>enc:</code> + Base64。校驗碼只用来偵測「貼錯 / 被改動 / 用別的方案產生」的密文，
 * 讓錯誤早點以明確訊息爆出來，而不是安靜地拿一串亂碼當密鑰去撞後端的 401。
 * 產生密文一律用 scripts/encrypt-key.js（npm run key:encrypt）。
 *
 * 為什麼寫成 CommonJS：這份實作要同時給瀏覽器（src/ 以 ESM import）與命令列腳本
 * （scripts/encrypt-key.js，Node 14 環境以 require 載入）使用，兩邊共用同一份程式碼，
 * 才不會出現「腳本加密出來的密文前端解不開」的漂移問題。
 *
 * :warning: 混淆鹽是寫在程式碼裡的固定常量，不是密鑰管理機制；能讀懂 JS 的人仍可還原
 * 出明文。它保證的是「倉庫裡沒有明文密鑰」，並不等同於「瀏覽器拿不到密鑰」
 * （要徹底做到後者，只能讓地圖 key 走 BFF 代簽名，見 README）。
 */

'use strict';

/** 密文前綴：一眼可辨識，同時刻意和舊版 AES 密文（v1:）區分開 */
var PREFIX = 'enc:';

/** 混淆鹽（固定常量；一旦改動，所有既有密文都會失效，必須重新產生） */
var XOR_SALT = 'qcommerce-h5/api-signature-key/2026';

/** 解不開時統一用這句：不帶任何密鑰或密文內容 */
var BROKEN_MESSAGE = '密文無法解開：內容不完整、已被改動，或不是用本專案的加密腳本（npm run key:encrypt）產生的';

/** 只接受標準 Base64，避免 atob / Buffer 對非法字元過於寬容 */
var BASE64_PATTERN = /^[A-Za-z0-9+/]+={0,2}$/;

/** 文字 → UTF-8 位元組（瀏覽器與 Node 都可用，能正確處理中文等非 ASCII 字元） */
function utf8ToBytes(text) {
  var escaped = unescape(encodeURIComponent(String(text)));
  var bytes = [];
  for (var i = 0; i < escaped.length; i++) {
    bytes.push(escaped.charCodeAt(i) & 0xff);
  }
  return bytes;
}

/** UTF-8 位元組 → 文字；位元組不是合法 UTF-8 時會拋錯（可用来判斷密文是否被改動） */
function bytesToUtf8(bytes) {
  var chars = '';
  for (var i = 0; i < bytes.length; i++) {
    chars += String.fromCharCode(bytes[i]);
  }
  return decodeURIComponent(escape(chars));
}

/** 2 位元組加總校驗碼（只防貼錯與改動，不是密碼學雜湊） */
function checksum(bytes) {
  var sum = 0;
  for (var i = 0; i < bytes.length; i++) {
    sum = (sum + bytes[i]) & 0xffff;
  }
  return [(sum >> 8) & 0xff, sum & 0xff];
}

/** 與混淆鹽做循環 XOR（加解密同一套運算） */
function xorWithSalt(bytes) {
  var salt = utf8ToBytes(XOR_SALT);
  var result = [];
  for (var i = 0; i < bytes.length; i++) {
    result.push(bytes[i] ^ salt[i % salt.length]);
  }
  return result;
}

function bytesToBase64(bytes) {
  var binary = '';
  for (var i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  if (typeof btoa === 'function') {
    return btoa(binary);
  }
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(binary, 'binary').toString('base64');
  }
  throw new Error('當前環境不支援 Base64 編碼');
}

function base64ToBytes(base64) {
  var binary;
  if (typeof atob === 'function') {
    binary = atob(base64);
  } else if (typeof Buffer !== 'undefined') {
    binary = Buffer.from(base64, 'base64').toString('binary');
  } else {
    throw new Error('當前環境不支援 Base64 解碼');
  }
  var bytes = [];
  for (var i = 0; i < binary.length; i++) {
    bytes.push(binary.charCodeAt(i) & 0xff);
  }
  return bytes;
}

/** 值是否為本方案的密文（只認 enc: 開頭） */
function isEncrypted(value) {
  return String(value === undefined || value === null ? '' : value).trim().indexOf(PREFIX) === 0;
}

/**
 * 加密共享密鑰。
 * @param {String} plaintext 明文密鑰
 * @returns {String} enc: + Base64
 */
function encryptKey(plaintext) {
  var value = String(plaintext === undefined || plaintext === null ? '' : plaintext).trim();
  if (!value) {
    throw new Error('明文密鑰為空：請提供要加密的共享密鑰（也可從 stdin 貼入）');
  }
  if (isEncrypted(value)) {
    throw new Error('輸入看起來已經是密文（' + PREFIX + ' 開頭），請提供明文密鑰，避免二次加密');
  }
  var bytes = utf8ToBytes(value);
  return PREFIX + bytesToBase64(xorWithSalt(checksum(bytes).concat(bytes)));
}

/**
 * 解密共享密鑰。錯誤訊息只說明格式，不含任何密鑰或密文內容。
 * @param {String} payload enc: 開頭的密文
 * @returns {String} 明文密鑰
 */
function decryptKey(payload) {
  var value = String(payload === undefined || payload === null ? '' : payload).trim();
  if (!value) {
    throw new Error('密文為空：請提供 ' + PREFIX + ' 開頭的密文');
  }
  if (!isEncrypted(value)) {
    throw new Error('密文格式非法：應以 "' + PREFIX + '" 開頭（可用 npm run key:encrypt 產生）');
  }

  var body = value.slice(PREFIX.length);
  if (!BASE64_PATTERN.test(body) || body.length % 4 !== 0) {
    throw new Error('密文內容非法：' + PREFIX + ' 之後必須是完整的標準 Base64');
  }

  var bytes;
  try {
    bytes = xorWithSalt(base64ToBytes(body));
  } catch (error) {
    throw new Error(BROKEN_MESSAGE);
  }
  if (bytes.length < 3) {
    throw new Error(BROKEN_MESSAGE);
  }

  var expected = bytes.slice(0, 2);
  var actual = checksum(bytes.slice(2));
  var matched = expected[0] === actual[0] && expected[1] === actual[1];
  if (!matched) {
    throw new Error(BROKEN_MESSAGE);
  }

  try {
    return bytesToUtf8(bytes.slice(2));
  } catch (error) {
    throw new Error(BROKEN_MESSAGE);
  }
}

module.exports = {
  PREFIX: PREFIX,
  isEncrypted: isEncrypted,
  encryptKey: encryptKey,
  decryptKey: decryptKey,
};
